/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { useAuth } from './useHooks';
import { PageContainerWithBreadcrumbs } from '../PageContainer/PageContainerWithBreadcrumbs';
import { selectPearlUserInfo } from './userSlice';
import { useAppSelector } from '../../../app/hooks';
import { analyticsTrack } from '../analytics_utils';

interface LoginCallbackWrapperProps {
  children: React.ReactElement;
}

export function LoginCallbackWrapper({ children }: LoginCallbackWrapperProps) {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const queryParams = new URLSearchParams(window.location.search);
  const authError = authState?.error?.message;
  const authErrorDescription = authState?.error_description;
  const error = queryParams.get('error') || (typeof authError === 'string' ? authError : undefined);
  const errorDescription =
    queryParams.get('error_description') ||
    (typeof authErrorDescription === 'string' ? authErrorDescription : undefined);

  const [showError, setShowError] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => {
      const hasError = authState?.isAuthenticated === false && !!error;
      console.log(
        'LoginCallbackWrapper: 1 second timeout: Now setting showError to:',
        hasError,
        'error is:',
        error,
        queryParams,
        authState
      );
      setShowError(hasError);
      if (hasError) {
        analyticsTrack(
          'login_failed',
          {
            title: error || 'Okta Error',
            description: errorDescription || '',
          },
          undefined
        );
      } else {
        analyticsTrack('login_succeeded', {}, pearlUserInfo);
      }
    }, 1000);

    return () => {
      // cancel the timeout in the cleanup function
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.isAuthenticated, authState?.error]);

  if (showError) {
    console.error(
      `TopLayout: in /login/callback but not authenticated ${JSON.stringify(
        authState
      )}. Error is: ${error} ${errorDescription}`
    );

    return (
      <PageContainerWithBreadcrumbs breadcrumbsProps={{ goBackLink: '/', children: <span>Login Failure</span> }}>
        <h1>Failed to login</h1>
        <p>
          You are not logged in. Please try again. If the problem persists, please contact your system administrator.
        </p>
        {error && (
          <p>
            <code>{error}</code>: <code>{errorDescription}</code>
          </p>
        )}
        <p>
          Suggestion: Please close this window and try signing in again from the <a href="/">MedPearl home page</a>. If
          you are still having issues, please contact{' '}
          <a href="mailto:medpearlsupport@healthendeavors.com">medpearlsupport@healthendeavors.com</a>.
        </p>
        <button onClick={() => navigate('/')}>Login</button>
        {children}
      </PageContainerWithBreadcrumbs>
    );
  }
  return children;
}
